import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appraisalState } from "../appraisal/appraisalSlice";
import { getStatusCountUserAssignedAppraisal } from "../../api/appraisalApi";
import useAuth from "../../hooks/useAuth";
import { Button } from "../../components/Button";
import Input from "../../components/Input";
import DateInput from "../../components/DateInput";
import moment from "moment";
import Loader from "../../components/Loader";
import { showNotification } from "../../components/Notification";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Assignment,
  CheckCircle,
  Schedule,
  TrendingUp,
  Autorenew,
  CloudDownload,
  Analytics,
  Assessment,
} from "@mui/icons-material";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import * as XLSX from "xlsx";

// Enhanced Metric Card Component
const EnhancedMetricCard = ({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  color = "primary",
  subTitle,
}) => {
  const colorConfigs = {
    primary: {
      bg: "linear-gradient(to bottom right, #0b3a5c, #135e96, #1f7fbb)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(19,94,150,0.35)",
    },
    success: {
      bg: "linear-gradient(to bottom right, #0f766e, #0d9488, #14b8a6)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(20,184,166,0.28)",
    },
    warning: {
      bg: "linear-gradient(to bottom right, #B45309, #D97706, #F5A623)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(245,166,35,0.28)",
    },
    error: {
      bg: "linear-gradient(to bottom right, #14532d, #166534, #22c55e)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(34,197,94,0.26)",
    },
    info: {
      bg: "linear-gradient(to bottom right, #5b21b6, #6d28d9, #8b5cf6)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(107,70,193,0.26)",
    },
  };

  const config = colorConfigs[color];

  return (
    <Card
      className="h-full transition-all duration-300 hover:scale-105 hover:shadow-2xl group overflow-hidden relative"
      style={{
        background: config.bg,
        boxShadow: config.shadow,
        borderRadius: "16px",
        border: "none",
        minHeight: "100px",
      }}
    >
      <CardContent className="p-6 relative z-10 h-full">
        <div className="flex flex-col h-full">
          {/* Title */}
          <Typography
            variant="body2"
            className="text-white text-opacity-90 font-medium mb-2"
            style={{ fontSize: "14px" }}
          >
            {title}
          </Typography>

          {/* Main Content */}
          <div className="flex-1 flex items-center justify-between">
            {/* Value */}
            <div>
              <Typography
                variant="h3"
                className="font-bold text-white"
                style={{
                  fontSize: "32px",
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  lineHeight: 1.2,
                }}
              >
                {value || 0}
              </Typography>

              {/* Change indicator */}
              {change !== undefined && (
                <Typography
                  variant="body2"
                  className="text-white text-opacity-90 mt-1"
                  style={{ fontSize: "12px" }}
                >
                  {(() => {
                    switch (changeType) {
                      case "increase":
                        return "+";
                      case "decrease":
                        return "-";
                      default:
                        return "";
                    }
                  })()}
                  {change}% vs last period
                </Typography>
              )}
            </div>

            {/* Icon */}
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
              style={{
                background: config.iconBg,
                backdropFilter: "blur(10px)",
              }}
            >
              <Icon style={{ color: "white", fontSize: "24px" }} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

EnhancedMetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  change: PropTypes.number,
  changeType: PropTypes.oneOf(["increase", "decrease", "neutral"]),
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.oneOf(["primary", "success", "warning", "error", "info"]),
  subTitle: PropTypes.string,
};

const EmpDashBoard = () => {
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const { loading, error, userDataCount } = useSelector(appraisalState);
  const {
    poor = 0,
    fair = 0,
    average = 0,
    good = 0,
    excellent = 0,
    notStarted = 0,
    inProgress = 0,
    needDiscussion = 0,
    completed = 0,
    underReview = 0,
    approved = 0,
    finalReviewed = 0,
    changeRequest = 0,
  } = userDataCount;

  const [dates, setDates] = useState({
    startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [isLoading, setIsLoading] = useState(false);
  const lastErrorRef = useRef(null);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDates((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleStartDateChange = (date) => {
    if (date) {
      setDates((prev) => ({
        ...prev,
        startDate: moment(date).format("YYYY-MM-DD"),
      }));
    } else {
      setDates((prev) => ({
        ...prev,
        startDate: "",
      }));
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      setDates((prev) => ({
        ...prev,
        endDate: moment(date).format("YYYY-MM-DD"),
      }));
    } else {
      setDates((prev) => ({
        ...prev,
        endDate: "",
      }));
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    const formattedFromDate = moment(dates.startDate)
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss.SSSSSS");
    const formattedToDate = moment(dates.endDate)
      .endOf("day")
      .format("YYYY-MM-DD HH:mm:ss.SSSSSS");

    try {
      await dispatch(
        getStatusCountUserAssignedAppraisal({
          userId: auth.user.id,
          startDate: formattedFromDate,
          endDate: formattedToDate,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setDates({
      startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
    });
    dispatch(
      getStatusCountUserAssignedAppraisal({
        userId: auth.user.id,
      })
    );
  };

  const calculatePercentage = (count) => {
    const totalCount = poor + fair + good + average + excellent;

    return totalCount > 0 ? ((count / totalCount) * 100).toFixed(2) : "0.00";
  };

  // Export dashboard data to Excel
  const handleExportToExcel = () => {
    const exportData = [
      { Metric: "Total Appraisals", Count: notStarted + inProgress + completed + underReview + approved + finalReviewed + changeRequest + needDiscussion },
      { Metric: "", Count: "" }, // Empty row
      { Metric: "Appraisal Status", Count: "" },
      { Metric: "Not Started", Count: notStarted },
      { Metric: "In Progress", Count: inProgress },
      { Metric: "Completed", Count: completed },
      { Metric: "Under Review", Count: underReview },
      { Metric: "Approved", Count: approved },
      { Metric: "Change Request", Count: changeRequest },
      { Metric: "Need Discussion", Count: needDiscussion },
      { Metric: "Final Reviewed", Count: finalReviewed },
      { Metric: "", Count: "" }, // Empty row
      { Metric: "Performance Ratings", Count: "" },
      { Metric: "Excellent", Count: excellent },
      { Metric: "Exceeds Expectations", Count: good },
      { Metric: "Meets Expectations", Count: average },
      { Metric: "Needs Improvement", Count: fair },
      { Metric: "Unsatisfactory", Count: poor },
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    worksheet["!cols"] = [{ wch: 30 }, { wch: 15 }];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Dashboard");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `employee_dashboard_data_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, filename);
  };

  const statusData = useMemo(() => {
    const statusArray = [
      { name: "Not Started", value: notStarted || 0 },
      { name: "In Progress", value: inProgress || 0 },
      { name: "Pending Manager Review", value: completed || 0 },
      { name: "Under Review", value: underReview || 0 },
      { name: "Need Discussion", value: needDiscussion || 0 },
      { name: "Change Request", value: changeRequest || 0 },
      { name: "Pending Final Review", value: approved || 0 },
      { name: "Final Review", value: finalReviewed || 0 },
    ];

    // Return all statuses regardless of value (show all statuses)
    return statusArray;
  }, [notStarted, inProgress, completed, underReview, approved, changeRequest, needDiscussion, finalReviewed]);

  // Performance trends data from API
  const performanceTrendData = useMemo(() => {
    const excellentCount = Number(excellent) || 0;
    const goodCount = Number(good) || 0;
    const averageCount = Number(average) || 0;
    const fairCount = Number(fair) || 0;
    const poorCount = Number(poor) || 0;
    
    // Calculate total for percentage calculation (include all ratings)
    const total = excellentCount + goodCount + averageCount + fairCount + poorCount;
    
    // Always return data to show all ratings, even if all are 0
    return [
      {
        name: "Current Period",
        excellent: total > 0 ? Number(((excellentCount / total) * 100).toFixed(1)) : 0,
        good: total > 0 ? Number(((goodCount / total) * 100).toFixed(1)) : 0,
        average: total > 0 ? Number(((averageCount / total) * 100).toFixed(1)) : 0,
        fair: total > 0 ? Number(((fairCount / total) * 100).toFixed(1)) : 0,
        poor: total > 0 ? Number(((poorCount / total) * 100).toFixed(1)) : 0,
        // Store original counts for tooltip
        excellentCount: excellentCount,
        goodCount: goodCount,
        averageCount: averageCount,
        fairCount: fairCount,
        poorCount: poorCount,
      }
    ];
  }, [excellent, good, average, fair, poor]);

  const dashboardMetrics = useMemo(() => {
    const totalAppraisals =
      notStarted +
      inProgress +
      changeRequest +
      needDiscussion +
      completed +
      underReview +
      approved +
      finalReviewed;

    return {
      totalAppraisals,
      completed,
      inProgress,
      totalApproved: approved || 0,
    };
  }, [
    notStarted,
    inProgress,
    changeRequest,
    needDiscussion,
    completed,
    underReview,
    approved,
    finalReviewed,
  ]);

  useEffect(() => {
    if (auth?.user?.id) {
      dispatch(getStatusCountUserAssignedAppraisal({ userId: auth.user.id }));
    }
  }, [auth?.user?.id, dispatch]);

  useEffect(() => {
    // Only show error if it's a real error and not during loading
    // Also prevent showing the same error multiple times
    if (error && !loading && error !== "An error occurred" && error !== lastErrorRef.current) {
      const errorMessage = typeof error === 'string' ? error : error?.message || "An error occurred while loading dashboard data";
      // Only show meaningful errors, not generic ones
      if (errorMessage && !errorMessage.toLowerCase().includes("an error occurred")) {
        showNotification("error", errorMessage);
        lastErrorRef.current = error;
      }
    }
    // Clear the error ref when error is cleared
    if (!error) {
      lastErrorRef.current = null;
    }
  }, [error, loading]);

  return (
    <div className="min-h-screen p-4 md:p-6">
      {loading && <Loader />}

      {/* Date Filter Section - Same as Manager Dashboard */}
   

      {/* Metrics Cards */}
      <Box className="mb-8">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          <EnhancedMetricCard
            title="Total Appraisals"
            value={dashboardMetrics.totalAppraisals}
            icon={Assignment}
            color="primary"
          />
          <EnhancedMetricCard
            title="Completed"
            value={dashboardMetrics.completed}
            icon={CheckCircle}
            color="success"
          />
          <EnhancedMetricCard
            title="In Progress"
            value={dashboardMetrics.inProgress}
            icon={Schedule}
            color="warning"
          />
          <EnhancedMetricCard
            title="Pending Final Review"
            value={dashboardMetrics.totalApproved}
            icon={TrendingUp}
            color="info"
          />
        </Box>
      </Box>

      {/* Charts Section - Employee Dashboard Specific */}
      <Box className="mb-8">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "1fr 1fr",
            },
            gap: 3,
          }}
        >
          {/* Performance Trends Chart */}
          {performanceTrendData.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card
                className="h-full overflow-hidden relative group"
                style={{
                  background: "white",
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease",
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-blue-50/30 to-teal-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <motion.div
                        className="p-3 rounded-xl bg-gradient-to-br from-green-500 via-blue-600 to-teal-500 shadow-lg"
                        whileHover={{ scale: 1.1, rotate: -5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <Analytics style={{ color: "white", fontSize: "24px" }} />
                      </motion.div>
                      <div>
                        <Typography variant="h6" className="font-bold text-gray-800 mb-0.5">
                          Performance Trends
                        </Typography>
                        <Typography variant="caption" className="text-gray-500 text-xs">
                          Historical performance metrics
                        </Typography>
                      </div>
                    </div>
                    <Tooltip title="View detailed trends">
                      <IconButton
                        size="small"
                        className="bg-gradient-to-r from-green-100 to-blue-100 hover:from-green-200 hover:to-blue-200 transition-all duration-200 shadow-sm"
                      >
                        <Analytics style={{ color: "#10B981", fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="h-[420px] min-h-[420px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={performanceTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <defs>
                          <linearGradient
                            id="excellentGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop offset="5%" stopColor="#11998e" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#11998e" stopOpacity={0.3} />
                          </linearGradient>
                          <linearGradient id="goodGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#667eea" stopOpacity={0.3} />
                          </linearGradient>
                          <linearGradient
                            id="averageGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop offset="5%" stopColor="#f093fb" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#f093fb" stopOpacity={0.3} />
                          </linearGradient>
                          <linearGradient
                            id="fairGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop offset="5%" stopColor="#FFA726" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#FFA726" stopOpacity={0.3} />
                          </linearGradient>
                          <linearGradient
                            id="poorGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop offset="5%" stopColor="#EF5350" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#EF5350" stopOpacity={0.3} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                        <XAxis
                          dataKey="name"
                          tick={{ fill: "#6b7280", fontSize: 12 }}
                          axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                        />
                        <YAxis
                          tick={{ fill: "#6b7280", fontSize: 12 }}
                          axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                          label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280' } }}
                          domain={[0, 100]}
                        />
                        <RechartsTooltip
                          formatter={(value, name, props) => {
                            const payload = props.payload;
                            let count = 0;
                            if (name === "Excellent") {
                              count = payload.excellentCount || 0;
                            } else if (name === "Exceeds Expectations") {
                              count = payload.goodCount || 0;
                            } else if (name === "Meets Expectations") {
                              count = payload.averageCount || 0;
                            } else if (name === "Needs Improvement") {
                              count = payload.fairCount || 0;
                            } else if (name === "Unsatisfactory") {
                              count = payload.poorCount || 0;
                            }
                            return [`${count} (${value}%)`, name];
                          }}
                          labelFormatter={(label) => label}
                          contentStyle={{
                            background: "rgba(255,255,255,0.95)",
                            border: "none",
                            borderRadius: "12px",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                            backdropFilter: "blur(10px)",
                          }}
                        />
                        <Bar
                          dataKey="excellent"
                          fill="url(#excellentGradient)"
                          radius={[4, 4, 0, 0]}
                          name="Excellent"
                        />
                        <Bar
                          dataKey="good"
                          fill="url(#goodGradient)"
                          radius={[4, 4, 0, 0]}
                          name="Exceeds Expectations"
                        />
                        <Bar
                          dataKey="average"
                          fill="url(#averageGradient)"
                          radius={[4, 4, 0, 0]}
                          name="Meets Expectations"
                        />
                        <Bar
                          dataKey="fair"
                          fill="url(#fairGradient)"
                          radius={[4, 4, 0, 0]}
                          name="Needs Improvement"
                        />
                        <Bar
                          dataKey="poor"
                          fill="url(#poorGradient)"
                          radius={[4, 4, 0, 0]}
                          name="Unsatisfactory"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap justify-center gap-4 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#11998e" }}></div>
                      <Typography variant="caption" className="text-gray-600 font-medium">
                        Excellent
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#667eea" }}></div>
                      <Typography variant="caption" className="text-gray-600 font-medium">
                        Exceeds Expectations
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#f093fb" }}></div>
                      <Typography variant="caption" className="text-gray-600 font-medium">
                        Meets Expectations
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#FFA726" }}></div>
                      <Typography variant="caption" className="text-gray-600 font-medium">
                        Needs Improvement
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#EF5350" }}></div>
                      <Typography variant="caption" className="text-gray-600 font-medium">
                        Unsatisfactory
                      </Typography>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <Card
              className="h-full overflow-hidden relative"
              style={{
                background: "white",
                borderRadius: "24px",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-blue-50 opacity-50"></div>
              <CardContent className="p-6 relative z-10 flex flex-col items-center justify-center h-[420px]">
                <motion.div
                  className="w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-blue-100 flex items-center justify-center mb-4"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  <Analytics style={{ color: "#10B981", fontSize: "40px" }} />
                </motion.div>
                <Typography variant="body2" className="text-gray-500 font-medium mb-1">
                  No performance trend data available
                </Typography>
                <Typography variant="caption" className="text-gray-400 text-xs">
                  Historical performance data will be displayed here once available
                </Typography>
              </CardContent>
            </Card>
          )}
          {/* Appraisal Progress Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card
              className="h-full group"
              style={{
                background: "white",
                borderRadius: "24px",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-blue-50/30 to-pink-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 via-blue-600 to-pink-500 shadow-lg">
                    <Assessment style={{ color: "white", fontSize: "24px" }} />
                  </div>
                  <div>
                    <Typography variant="h6" className="font-bold text-gray-800 mb-0.5">
                      Appraisal Progress Overview
                    </Typography>
                    <Typography variant="caption" className="text-gray-500 text-xs">
                      Status breakdown by percentage
                    </Typography>
                  </div>
                </div>
                <div className="space-y-5">
                  {statusData.length > 0 ? (
                    statusData.map((status, index) => {
                      const total = statusData.reduce(
                        (sum, item) => sum + item.value,
                        0
                      );
                      const percentage =
                        total > 0 ? (status.value / total) * 100 : 0;
                      
                      // Use the same colors as in table data (from Utils.js)
                      const statusColorMap = {
                        "Not Started": "#B0B0B0", // Gray
                        "In Progress": "#2196F3", // Blue
                        "Pending Manager Review": "#009688", // Teal (was COMPLETED)
                        "Under Review": "#FF9800", // Orange
                        "Need Discussion": "#6e07f4", // Purple (NEED_DISCUSSION)
                        "Change Request": "#F44336", // Red
                        "Pending Final Review": "#66BB6A", // Green (was APPROVED)
                        "Final Review": "#9C27B0", // Purple (FINAL_REVIEWED)
                      };
                      
                      const color = statusColorMap[status.name] || "#8B5CF6";

                      return (
                        <Tooltip
                          key={status.name}
                          title={`Click to filter by ${status.name}`}
                          arrow
                          placement="top"
                        >
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="group/item cursor-pointer"
                            onClick={() => {
                              console.log(`Filter by status: ${status.name}`);
                              // TODO: Implement navigation to filtered appraisal list
                            }}
                            whileHover={{ x: 4 }}
                          >
                        <div className="flex justify-between items-center mb-2">
                          <Typography
                            variant="body2"
                            className="font-semibold text-gray-700 group-hover/item:text-[#135e96] transition-colors"
                            style={{ fontSize: "14px" }}
                          >
                            {status.name}
                          </Typography>
                            <div className="flex items-center gap-2">
                              <Typography
                                variant="body2"
                                className="font-bold"
                                style={{ color, fontSize: "14px" }}
                              >
                                {status.value}
                              </Typography>
                              <Typography
                                variant="body2"
                                className="text-gray-500"
                                style={{ fontSize: "12px" }}
                              >
                                ({percentage.toFixed(1)}%)
                              </Typography>
                            </div>
                          </div>
                        <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full group-hover/item:opacity-90 transition-opacity"
                            style={{ backgroundColor: color }}
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 1, delay: index * 0.1 }}
                          />
                          <div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                            style={{
                              animation: "shimmer 2s infinite",
                            }}
                          ></div>
                        </div>
                      </motion.div>
                      </Tooltip>
                    );
                    })
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                        <Assessment style={{ color: "#8B5CF6", fontSize: "32px" }} />
                      </div>
                      <Typography variant="body2" className="text-gray-500 font-medium">
                        No appraisal data available
                      </Typography>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </Box>
      </Box>
    </div>
  );
};

export default EmpDashBoard;
